import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  AdjudicationReadsView,
  Annotation,
  BiopsyViews,
  Case,
  CaseWithImages,
  FixedQueryCategory,
  Image,
  ImageWithAnnotations,
  Indication,
  MultilineAnnotation,
  OtherQueryCategory,
  QueryObjectType,
  MultilineCategory,
  MultilineCell,
  Fragment,
  PointAnnotation,
  AnnotationSource,
  DeficientFeatureOption,
  ImageQualityAssessment,
  ImageQualityAssessmentSubmission
} from "../models";
import {
  addComment,
  closeQueryApi,
  completeRead as completeReadApi,
  fetchImageQualityAssessment as fetchImageQualityAssessmentApi,
  saveImageQualityAssessment as saveImageQualityAssessmentApi,
  fetchDeficientFeatureOptions as fetchDeficientFeatureOptionsApi,
  createAnnotationApi,
  createQuery,
  deleteAnnotationApi,
  fetchAdjudicationReads,
  fetchCase,
  fetchCaseBiopsies,
  selectCaseAdjudication as selectCaseAdjudicationApi,
  selectImageAdjudication as selectImageAdjudicationApi,
  fetchImage,
  forwardCaseStatus,
  markQueryUnresolvable,
  newQueryFollowUpReminder,
  passQcForImage,
  patchAcceptAiAnnotations,
  reorderCaseImages,
  toggleImageVisibility
} from "../api";
import { Resource, WriteResource } from "../types";
import { LatLngLiteral } from "leaflet";

import {
  FreehandAnnotationClass,
  MultilineAnnotationClass,
  PointAnnotationClass,
  HpfAnnotationClass,
  UUID
} from "../models";
import { RootState } from "../store";
import {
  pointToGeoJSON,
  polygonToGeoJSON,
  lineToGeoJSON,
  getNextUnusedMultilineCell,
  mapStateToFragments
} from "../utils";
import { castDraft } from "immer";
import { redirectAction } from "./auth";

export type QueryEditorForm =
  | {
      readonly category: FixedQueryCategory | null;
    }
  | {
      readonly category: OtherQueryCategory;
      readonly text: string;
    };

export interface CaseImageViewerState {
  readonly histoCase: Resource<CaseWithImages> | null;
  readonly imageWithAnnotations: Resource<ImageWithAnnotations> | null;
  readonly annotationCacheKeyAsUnixTs: number;
  readonly hideAnnotations: boolean;
  readonly allHighlightedAnnotations: ReadonlyArray<Annotation>;
  readonly highlightedAnnotations: ReadonlyArray<string>;
  readonly brightnessAdjust: number;
  readonly annotationOpacity: number | null;
  readonly queryEditor: QueryEditorForm | null;
  readonly annotation: WriteResource<AnnotationForm<SelectedAnnotationType> | null, Annotation>;
  readonly tempHpf: LatLngLiteral | null;
  readonly hpfCursorColor: string | null;
  readonly isSidebarExpanded: boolean;
  readonly isMicroscopeActive: boolean;
  readonly selectedMultilineClass: MultilineAnnotationClass | null;
  readonly selectedMultilineCategory: MultilineCategory | null;
  readonly manualHpfVisibility: ReadonlyArray<HpfVisibility>;
  readonly aiHpfVisibility: ReadonlyArray<HpfVisibility>;
  readonly celiacTableVisibility: boolean;
  readonly zoom: number;
  // FE-05: the case's biopsy groupings (BE-03), fetched from the backend for the image selector.
  readonly biopsies: BiopsyViews;
  // FE-06: the adjudicator's read-switching + selection state (blinded CR reads). Null when the
  // current user is not the case's adjudicator.
  readonly adjudicationReads: AdjudicationReadsView | null;
  // FE-06: which CR read the adjudicator is currently viewing (crUserId), or null for the
  // adjudicator's OWN read (Hybrid) / a normal reader.
  readonly selectedCrUserId: UUID | null;
  // IQA (Hybrid + IQA-enabled studies): the editable "Deficient Features" options and the reader's
  // loaded saved IQA for the currently-selected image (null resource = no IQA recorded yet). Save
  // status is tracked separately so an in-flight/failed save does not clobber the loaded value.
  readonly deficientFeatureOptions: ReadonlyArray<DeficientFeatureOption>;
  readonly imageQualityAssessment: Resource<ImageQualityAssessment | null> | null;
  readonly imageQualityAssessmentSaving: boolean;
  readonly imageQualityAssessmentSaveError: string | null;
}

export const initialState: CaseImageViewerState = {
  histoCase: {
    isPending: false
  },
  imageWithAnnotations: {
    isPending: false
  },
  annotationCacheKeyAsUnixTs: 1,
  hideAnnotations: false,
  allHighlightedAnnotations: [],
  highlightedAnnotations: [],
  brightnessAdjust: 0,
  annotationOpacity: 100,
  queryEditor: null,
  annotation: {
    data: null
  },
  tempHpf: null,
  hpfCursorColor: null,
  isSidebarExpanded: true,
  isMicroscopeActive: false,
  selectedMultilineClass: null,
  selectedMultilineCategory: null,
  manualHpfVisibility: [],
  aiHpfVisibility: [],
  celiacTableVisibility: true,
  zoom: 0,
  biopsies: [],
  adjudicationReads: null,
  selectedCrUserId: null,
  deficientFeatureOptions: [],
  imageQualityAssessment: null,
  imageQualityAssessmentSaving: false,
  imageQualityAssessmentSaveError: null
};

/* annotations  */

// This form is for annotation types that can be _edited_ (moved around or text added). Several
// types of annotations cannot be edited.
export interface BaseAnnotationForm {
  readonly geometry: LatLngLiteral;
}
export type HPFAnnotationForm = BaseAnnotationForm & {
  readonly radiusX: number;
  readonly text: string | null;
  readonly tempHpf: LatLngLiteral | null;
};
export type EllipseAnnotationForm = BaseAnnotationForm & {
  readonly radiusX: number;
  readonly radiusY: number;
  readonly tilt: number;
  readonly text: string | null;
};
export type TextAnnotationForm = BaseAnnotationForm & {
  readonly text: string;
};

export enum AnnotationStatus {
  Dropping = "DROPPING",
  Placing = "PLACING",
  Editing = "EDITING"
}

export enum AnnotationType {
  Line = "LINE",
  Multiline = "MULTILINE",
  Ellipse = "ELLIPSE",
  HPF = "HPF",
  Text = "TEXT",
  Point = "POINT",
  Freehand = "FREEHAND"
}

export interface LineAnnotationType {
  readonly type: AnnotationType.Line;
}

export interface MultilineAnnotationType {
  readonly type: AnnotationType.Multiline;
}

export interface EllipseAnnotationType {
  readonly type: AnnotationType.Ellipse;
}

export interface TextAnnotationType {
  readonly type: AnnotationType.Text;
}

// NOTE: These annotation types are special in that they have extra info associated with them
// (annotation class, which determines their color).
export interface HpfAnnotationType {
  readonly type: AnnotationType.HPF;
  readonly hpfAnnotationClass: HpfAnnotationClass | null;
}

export interface PointAnnotationType {
  readonly type: AnnotationType.Point;
  readonly pointAnnotationClass: PointAnnotationClass | null;
}

export interface FreehandAnnotationType {
  readonly type: AnnotationType.Freehand;
  readonly freehandAnnotationClass: FreehandAnnotationClass | null;
}

export interface MultilineAnnotationType {
  readonly type: AnnotationType.Multiline;
  readonly multilineAnnotationClass: MultilineAnnotationClass | null;
  readonly multilineAnnotationCategory: MultilineCategory | null;
}

export type SelectedAnnotationType = EditableAnnotationType | UneditableAnnotationType;

export type EditableAnnotationType = EllipseAnnotationType | HpfAnnotationType | TextAnnotationType;

// NOTE: These annotation types cannot be edited; they are placed/drawn and saved directly
export type UneditableAnnotationType =
  | PointAnnotationType
  //| HpfAnnotationType
  | LineAnnotationType
  | MultilineAnnotationType
  | FreehandAnnotationType;

export type FormFromEditableAnnotationType<
  T extends EditableAnnotationType
> = T extends EllipseAnnotationType
  ? EllipseAnnotationForm
  : T extends TextAnnotationType
  ? TextAnnotationForm
  : T extends HpfAnnotationType
  ? HPFAnnotationForm
  : never;

export type AnnotationForm<T extends SelectedAnnotationType> =
  | {
      readonly form: null;
      readonly status: AnnotationStatus.Placing;
      readonly selectedAnnotationType: T;
    }
  | {
      readonly form: null;
      readonly status: AnnotationStatus.Dropping;
      readonly tempHpf: LatLngLiteral | null;
      readonly selectedAnnotationType: T;
    }
  | {
      readonly form: FormFromEditableAnnotationType<Exclude<T, UneditableAnnotationType>>;
      readonly status: AnnotationStatus.Editing;
      readonly selectedAnnotationType: Exclude<T, UneditableAnnotationType>;
    };

export type HpfVisibility = {
  readonly hpfClassid: string | null;
  readonly hpfId: string | null;
  readonly pointClassId: string | null;
};

export type HpfVisibilityPayload = {
  readonly source: AnnotationSource;
  readonly hpfVisibility: HpfVisibility;
  readonly fragment: Fragment;
};

function highlightAllImage(
  iwa: ImageWithAnnotations,
  ids: readonly string[]
): readonly Annotation[] {
  return iwa.annotations.map((anno: Annotation) => {
    return anno !== null
      ? ids.includes(anno.id)
        ? { ...anno, weight: 10 }
        : { ...anno, weight: 5 }
      : anno;
  });
}

function highlightAll(
  iwa: Resource<ImageWithAnnotations> | null,
  ids: readonly string[]
): readonly Annotation[] {
  return iwa && "resource" in iwa ? highlightAllImage(iwa.resource, ids) : [];
}

function addId(annos: readonly string[], id: string): readonly string[] {
  if (annos.find(e => e === id)) {
    return annos;
  } else {
    return [...annos, id];
  }
}

function removeId(annos: readonly string[], id: string): readonly string[] {
  return annos.filter(anno => anno !== id);
}

// thunks
export const caseFetch = createAsyncThunk(
  "caseImageViewer/caseFetch",
  async (caseId: string, thunkApi) => {
    const { dispatch } = thunkApi;
    const fetchCaseResponse = await fetchCase(caseId);

    const imageToFetchId =
      "images" in fetchCaseResponse && fetchCaseResponse.images.length > 0
        ? fetchCaseResponse.images[0]?.id
        : "imageQueries" in fetchCaseResponse &&
          fetchCaseResponse.imageQueries[0] &&
          fetchCaseResponse.imageQueries[0][0]
        ? fetchCaseResponse.imageQueries[0][0].image.id
        : null;

    if (imageToFetchId) {
      await dispatch(imageFetch(imageToFetchId));
    }
    await dispatch(biopsiesFetch(caseId));
    await dispatch(adjudicationReadsFetch(caseId));
    return fetchCaseResponse;
  }
);

// FE-05: fetch the case's biopsy groupings (BE-03) from the backend for the image selector.
export const biopsiesFetch = createAsyncThunk(
  "caseImageViewer/biopsiesFetch",
  async (caseId: string) => {
    return fetchCaseBiopsies(caseId);
  }
);

// IQA: load the editable "Deficient Features" options once (for the multi-select).
export const deficientFeatureOptionsFetch = createAsyncThunk(
  "caseImageViewer/deficientFeatureOptionsFetch",
  async () => fetchDeficientFeatureOptionsApi()
);

// IQA: load the reader's saved IQA for the given image so the form can pre-fill (null = none yet).
// The caseId is threaded in from the caller (route params) rather than read from histoCase: on
// initial load caseFetch resolves the image (and so selectedImageId) BEFORE it sets histoCase, so a
// histoCase guard here would reject the very first fetch and the form would never appear until the
// user switched images.
export interface ImageQualityAssessmentFetchParams {
  readonly caseId: UUID;
  readonly imageId: UUID;
}
export const imageQualityAssessmentFetch = createAsyncThunk(
  "caseImageViewer/imageQualityAssessmentFetch",
  async (params: ImageQualityAssessmentFetchParams) =>
    fetchImageQualityAssessmentApi(params.caseId, params.imageId)
);

// IQA: save (upsert) the reader's IQA for an image. ImageQualityAssessment and its Submission are
// structurally identical, so resolve with the submitted data directly instead of a second GET round
// trip — the persisted row is what we just wrote.
export interface ImageQualityAssessmentSaveParams {
  readonly caseId: UUID;
  readonly imageId: UUID;
  readonly submission: ImageQualityAssessmentSubmission;
}
export const imageQualityAssessmentSave = createAsyncThunk(
  "caseImageViewer/imageQualityAssessmentSave",
  async (params: ImageQualityAssessmentSaveParams): Promise<ImageQualityAssessment> => {
    await saveImageQualityAssessmentApi(params.caseId, params.imageId, params.submission);
    return params.submission;
  }
);

// FE-06: fetch the adjudicator's read-switching + selection state. Rejects (→ null in the reducer)
// when the current user is not the case's adjudicator, so non-adjudicators simply see no switcher.
export const adjudicationReadsFetch = createAsyncThunk(
  "caseImageViewer/adjudicationReadsFetch",
  async (caseId: string) => {
    return fetchAdjudicationReads(caseId);
  }
);

// FE-06: switch which read the adjudicator is viewing. `crUserId = null` selects the adjudicator's
// own read (Hybrid). Re-fetches the current image scoped to the chosen read.
export const switchAdjudicatorRead = createAsyncThunk(
  "caseImageViewer/switchAdjudicatorRead",
  async (crUserId: UUID | null, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    dispatch(setSelectedCrUserId(crUserId));
    const state = (getState() as RootState).caseImageViewer;
    const currentImageId =
      state.imageWithAnnotations && "resource" in state.imageWithAnnotations
        ? state.imageWithAnnotations.resource.imageAndQuery.image.id
        : null;
    if (currentImageId) {
      await dispatch(imageFetch(currentImageId));
    }
  }
);

// FE-06 / API-07: record the adjudicator's winning selection (case-level or biopsy-level), then
// refresh the reads/selection state so the UI reflects the new selection.
// reasonForChange is only required by the backend when CHANGING an already-recorded selection (a
// GxP decision of record); the UI collects it via a confirm dialog before dispatching in that case.
export interface RecordCaseAdjudicationParams {
  readonly winningAssignmentId: UUID;
  readonly reasonForChange?: string;
}
export const recordCaseAdjudication = createAsyncThunk(
  "caseImageViewer/recordCaseAdjudication",
  async (params: RecordCaseAdjudicationParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const caseId = adjudicationCaseId(getState() as RootState);
    if (caseId === null) {
      return Promise.reject(new Error("No case loaded"));
    }
    await selectCaseAdjudicationApi(caseId, params.winningAssignmentId, params.reasonForChange);
    await dispatch(adjudicationReadsFetch(caseId));
  }
);

export interface RecordImageAdjudicationParams {
  readonly imageId: UUID;
  readonly winningAssignmentId: UUID;
  readonly reasonForChange?: string;
}
export const recordImageAdjudication = createAsyncThunk(
  "caseImageViewer/recordImageAdjudication",
  async (params: RecordImageAdjudicationParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const caseId = adjudicationCaseId(getState() as RootState);
    if (caseId === null) {
      return Promise.reject(new Error("No case loaded"));
    }
    await selectImageAdjudicationApi(
      caseId,
      params.imageId,
      params.winningAssignmentId,
      params.reasonForChange
    );
    await dispatch(adjudicationReadsFetch(caseId));
  }
);

function adjudicationCaseId(rootState: RootState): UUID | null {
  const histoCase = rootState.caseImageViewer.histoCase;
  return histoCase && "resource" in histoCase ? histoCase.resource.caseWithStatus.id : null;
}

export const refreshCase = createAsyncThunk(
  "caseImageViewer/refreshCase",
  async (_: void, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const state = getState() as RootState;
    if (state.caseImageViewer.histoCase !== null && "resource" in state.caseImageViewer.histoCase) {
      const caseId = state.caseImageViewer.histoCase.resource.caseWithStatus.id;
      const fetchCaseResponse = await fetchCase(caseId);
      // Refresh the biopsy groupings too: they map imageIds → images, so a stale set silently omits
      // images added/replaced while the viewer is open once grouping applies (FE-05).
      await dispatch(biopsiesFetch(caseId));
      const updatedState = getState() as RootState;

      const selectedImageId =
        updatedState.caseImageViewer.imageWithAnnotations &&
        "resource" in updatedState.caseImageViewer.imageWithAnnotations
          ? updatedState.caseImageViewer.imageWithAnnotations.resource.imageAndQuery.image.id
          : null;

      const isSelectedImageInCase =
        selectedImageId !== null &&
        fetchCaseResponse.images.map(image => image.id).includes(selectedImageId);

      if (selectedImageId && isSelectedImageInCase) {
        await dispatch(refreshImage({ imageId: selectedImageId }));
      } else {
        if (fetchCaseResponse.images[0]) {
          await dispatch(selectImage(fetchCaseResponse.images[0].id));
        }
      }

      return fetchCaseResponse;
    }
  }
);

export interface RefreshImageParams {
  readonly imageId: UUID;
  readonly refreshCaseOnSuccess?: boolean;
}
export const refreshImage = createAsyncThunk(
  "caseImageViewer/refreshImage",
  async (params: RefreshImageParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;

    // FE-06: honour the adjudicator's selected CR read (blinded) when re-fetching.
    const state = (getState() as RootState).caseImageViewer;
    const response = await fetchImage(params.imageId, state.selectedCrUserId ?? undefined);
    if (params.refreshCaseOnSuccess) {
      await dispatch(refreshCase());
    }
    return response;
  }
);

export const transitionCaseStatus = createAsyncThunk(
  "caseImageViewer/transitionCaseStatus",
  async (c: Case, thunkApi) => {
    const { getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;
    if (state.histoCase !== null && "resource" in state.histoCase) {
      // This thunk drives the ISC "Pass QC" transition, whose result is Processed. Do NOT redirect
      // here: Pass QC keeps the ISC in the case viewer. The reader-facing redirect lives on the
      // completeRead thunk. (Previously this redirected on Processed and bounced the ISC out.)
      const forwardCaseStatusResponse = await forwardCaseStatus(
        state.histoCase.resource.caseWithStatus.id
      );

      return forwardCaseStatusResponse;
    }
  }
);

export const completeRead = createAsyncThunk(
  "caseImageViewer/completeRead",
  async (_: void, thunkApi) => {
    const { dispatch, getState, rejectWithValue } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;
    const embedded = rootState.config.embedded;
    if (state.histoCase === null || !("resource" in state.histoCase)) {
      // No loaded case to complete — reject so the failure surfaces instead of silently no-op'ing.
      return rejectWithValue("No case is loaded to complete.");
    }
    const caseId = state.histoCase.resource.caseWithStatus.id;
    const studyId = state.histoCase.resource.caseWithStatus.studyId;
    // Await the API before redirecting so a rejection (e.g. 409 already-completed) is handled by
    // the rejected reducer and no navigation happens on failure.
    const assignment = await completeReadApi(caseId);
    if (embedded) {
      dispatch(redirectAction("/successMessage"));
    } else {
      dispatch(redirectAction(`/studies/${studyId}`));
    }
    return assignment;
  }
);

export const toggleAcceptAiAnnotations = createAsyncThunk(
  "caseImageViewer/toggleAcceptAiAnnotations",
  async (imageId: UUID, _) => await patchAcceptAiAnnotations(imageId)
);

export const transitionImageStatus = createAsyncThunk(
  "caseImageViewer/transitionImageStatus",
  async (image: Image, thunkApi) => {
    const { dispatch } = thunkApi;
    const resp = await passQcForImage(image.id);
    await dispatch(refreshImage({ imageId: image.id }));
    return resp;
  }
);

export interface UpdateCaseComment {
  readonly caseId: string;
  readonly comment: string;
}

export interface UpdateImageComment {
  readonly caseId: string;
  readonly imageId: string;
  readonly comment: string;
}

export const updateCaseComment = createAsyncThunk(
  "caseImageViewer/updateCaseComment",
  async (params: UpdateCaseComment) => {
    const response = await addComment(params.caseId, null, params.comment);
    return response;
  }
);

export const updateImageComment = createAsyncThunk(
  "caseImageViewer/updateImageComment",
  async (params: UpdateImageComment) => {
    const response = await addComment(params.caseId, params.imageId, params.comment);
    return response;
  }
);

export interface OpenQueryParams {
  readonly queryObjectType: QueryObjectType;
  readonly objectId: UUID;
  readonly studyId: UUID;
  readonly caseId: UUID;
  readonly category: string;
  readonly categoryOtherText: string | null;
  readonly followUpInDays: number;
  readonly commentText: string | null;
  readonly withRoleId: UUID | null;
  readonly responseOptionId: UUID | null;
  readonly childResponseOptionId: string | null;
}

export const openQuery = createAsyncThunk(
  "caseImageViewer/openQuery",
  async (params: OpenQueryParams, thunkApi) => {
    const { dispatch } = thunkApi;

    const response = await createQuery(
      params.queryObjectType,
      params.objectId,
      params.studyId,
      params.caseId,
      params.category,
      params.categoryOtherText,
      params.followUpInDays,
      params.commentText,
      params.withRoleId,
      params.responseOptionId,
      params.childResponseOptionId
    );
    await dispatch(refreshCase());

    return response;
  }
);

export interface CloseQueryParams {
  readonly queryId: string;
  readonly detailedReasonText: string;
}

export const closeQuery = createAsyncThunk(
  "caseImageViewer/closeQuery",
  async (params: CloseQueryParams, thunkApi) => {
    const { dispatch } = thunkApi;

    const response = await closeQueryApi(params.queryId, params.detailedReasonText);
    await dispatch(refreshCase());
    return response;
  }
);

export interface UnresolvableQueryParams {
  readonly queryId: string;
  readonly unresolvableText: string;
  readonly refreshImageOnSuccess: UUID | null;
}

export const unresolvableQuery = createAsyncThunk(
  "caseImageViewer/unresolvableQuery",
  async (params: UnresolvableQueryParams, thunkApi) => {
    const { dispatch } = thunkApi;

    const response = await markQueryUnresolvable(params.queryId, params.unresolvableText);

    if (params.refreshImageOnSuccess) {
      await dispatch(refreshImage({ imageId: params.refreshImageOnSuccess }));
    } else {
      await dispatch(refreshCase());
    }

    return response;
  }
);

export interface NewQueryReminderParams {
  readonly queryId: string;
  readonly queryReminderId: string;
  readonly followUpInDays: number;
}

// annotation params
export interface BeginAnnotationParams {
  selectedAnnotationType: SelectedAnnotationType;
  imageId: UUID;
}

export interface BeginDroppingAnnotationParams {
  selectedAnnotationType: SelectedAnnotationType;
  imageId: UUID;
}
export interface SetImageAnnotationForEllipseTypeParams {
  point: LatLngLiteral;
  radiusX: number;
  radiusY: number;
  tilt: number;
}
export interface SetImageAnnotationForHPFTypeParams {
  point: LatLngLiteral;
  radius: number;
}

export interface CreateFreehandImageAnnotationParams {
  positions: ReadonlyArray<LatLngLiteral>;
  cleanupCallback: () => void;
}

export interface CreateLineImageAnnotationParams {
  points: ReadonlyArray<LatLngLiteral>;
  cleanupCallback: () => void;
}

export interface SelectNextUnusedMultilineClassParams {
  continue: boolean;
}

export interface CreateMultilineImageAnnotationParams {
  points: ReadonlyArray<LatLngLiteral>;
  cleanupCallback: () => void;
}

export interface CreatePointImageAnnotationParams {
  point: LatLngLiteral;
  hpfRadius: number;
  cleanupCallback: () => void;
}

// export interface CancelImageAnnotationParams {
//   point: LatLngLiteral;
//   radius: number;
//   cleanupCallback: () => void;
// }

// export interface CallbackParam {
//   cleanupCallback: () => void;
// }

// thunks
export const newQueryReminder = createAsyncThunk(
  "caseImageViewer/newQueryReminder",
  async (params: NewQueryReminderParams, thunkApi) => {
    const { dispatch } = thunkApi;

    const response = await newQueryFollowUpReminder(
      params.queryId,
      params.queryReminderId,
      params.followUpInDays
    );
    await dispatch(refreshCase());

    return response;
  }
);

export const selectImage = createAsyncThunk(
  "caseImageViewer/selectImage",
  async (imageId: UUID, thunkApi) => {
    const { dispatch } = thunkApi;
    await dispatch(refreshImage({ imageId: imageId }));
  }
);

export const imageFetch = createAsyncThunk(
  "caseImageViewer/imageFetch",
  async (imageId: UUID, thunkApi) => {
    // FE-06: when the adjudicator has switched to a CR's read, fetch that reader's (blinded)
    // annotations via crUserId; otherwise (own read / normal reader) fetch unscoped.
    const state = (thunkApi.getState() as RootState).caseImageViewer;
    const response = await fetchImage(imageId, state.selectedCrUserId ?? undefined);
    return response;
  }
);

export const toggleImageForCR = createAsyncThunk(
  "caseImageViewer/hideImageForReader",
  async (imageId: UUID) => {
    const response = await toggleImageVisibility(imageId);
    //TODO this would be better await dispatch(refreshCase());
    window.location.reload();
    return response;
  }
);

// annotation thunks
//
export const beginDroppingAnnotation = createAsyncThunk(
  "caseImageViewer/beginDroppingAnnotation",
  async (params: BeginDroppingAnnotationParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;

    if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
      dispatch(setBeginDroppingAnnotation(params));
      await dispatch(
        refreshImage({ imageId: state.imageWithAnnotations.resource.imageAndQuery.image.id })
      );
    }
  }
);

export const createFreehandImageAnnotation = createAsyncThunk(
  "caseImageViewer/createFreehandImageAnnotation",
  async (params: CreateFreehandImageAnnotationParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;

    const keepAnnotating = true;

    if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
      const createAnnotationResponse = await createAnnotationApi(
        polygonToGeoJSON(params.positions),
        null,
        null,
        null,
        null,
        state.annotation.data &&
          "freehandAnnotationClass" in state.annotation.data.selectedAnnotationType &&
          state.annotation.data.selectedAnnotationType.freehandAnnotationClass
          ? state.annotation.data.selectedAnnotationType.freehandAnnotationClass.id
          : null,
        "FREEHAND",
        state.imageWithAnnotations.resource.imageAndQuery.image.id,
        null
      );

      await dispatch(
        createImageAnnotationSuccess({
          keepAnnotating: keepAnnotating,
          cleanupCallback: params.cleanupCallback
        })
      );

      return createAnnotationResponse;
    }
  }
);

export const createLineImageAnnotation = createAsyncThunk(
  "caseImageViewer/createLineImageAnnotation",
  async (params: CreateLineImageAnnotationParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;

    const keepAnnotating = true;

    if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
      await createAnnotationApi(
        lineToGeoJSON(params.points),
        null,
        null,
        null,
        null,
        null,
        "LINE",
        state.imageWithAnnotations.resource.imageAndQuery.image.id,
        null
      );

      await dispatch(
        createImageAnnotationSuccess({
          keepAnnotating: keepAnnotating,
          cleanupCallback: params.cleanupCallback
        })
      );
    }
  }
);

export const selectNextUnusedMultilineClass = createAsyncThunk(
  "caseImageViewer/selectNextUnusedMultilineClass",
  (params: SelectNextUnusedMultilineClassParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;

    if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
      const annotations =
        state.imageWithAnnotations && "resource" in state.imageWithAnnotations
          ? state.imageWithAnnotations.resource.annotations.filter(a => !a.isAi)
          : [];
      const multilineAnnotations = annotations.filter(
        annotation => annotation.annotationType == AnnotationType.Multiline
      ) as MultilineAnnotation[];

      const selectedMultilineAnnotationClass = state.selectedMultilineClass;
      const selectedMultilineAnnotationCategory = state.selectedMultilineCategory;

      if (
        selectedMultilineAnnotationClass &&
        selectedMultilineAnnotationClass !== null &&
        "id" in selectedMultilineAnnotationClass &&
        selectedMultilineAnnotationCategory !== null
      ) {
        const annotation = multilineAnnotations.find(
          annotation =>
            annotation.annotationClassId === selectedMultilineAnnotationClass.id &&
            annotation.multilineCategory === selectedMultilineAnnotationCategory
        );
        if (annotation === undefined) {
          return;
        }
      }

      const start =
        params.continue &&
        selectedMultilineAnnotationClass !== null &&
        selectedMultilineAnnotationCategory !== null
          ? <MultilineCell>{
              class: selectedMultilineAnnotationClass,
              category: selectedMultilineAnnotationCategory
            }
          : undefined;

      const multilineCell = getNextUnusedMultilineCell(
        state.imageWithAnnotations.resource,
        undefined,
        start
      );

      if (multilineCell) {
        dispatch(setSelectedMultilineClass(multilineCell.class));
        dispatch(setSelectedMultilineCategory(multilineCell.category));
      }
    }
  }
);

export const createMultilineImageAnnotation = createAsyncThunk(
  "caseImageViewer/createMultilineImageAnnotation",
  async (params: CreateMultilineImageAnnotationParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;

    if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
      const exclude = <MultilineCell>{
        class: state.selectedMultilineClass,
        category: state.selectedMultilineCategory
      };

      const nextUnusedMultilineCell = getNextUnusedMultilineCell(
        state.imageWithAnnotations.resource,
        exclude,
        <MultilineCell>{
          class: state.selectedMultilineClass,
          category: state.selectedMultilineCategory
        }
      );

      const keepAnnotating = nextUnusedMultilineCell ? true : false;

      await createAnnotationApi(
        lineToGeoJSON(params.points),
        null,
        null,
        null,
        null,
        state.selectedMultilineClass ? state.selectedMultilineClass.id : null,
        "MULTILINE",
        state.imageWithAnnotations.resource.imageAndQuery.image.id,
        state.selectedMultilineCategory
      ).then(() => {
        if (!state.celiacTableVisibility) {
          dispatch(toggleCeliacTableVisibility());
        }
      });

      await dispatch(
        createImageAnnotationSuccess({
          keepAnnotating: keepAnnotating,
          cleanupCallback: params.cleanupCallback
        })
      );

      await dispatch(selectNextUnusedMultilineClass({ continue: true }));
    }
  }
);

export const createPointImageAnnotation = createAsyncThunk(
  "caseImageViewer/createPointImageAnnotation",
  async (params: CreatePointImageAnnotationParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;

    const keepAnnotating = true;

    const pointClass =
      state.annotation.data &&
      "pointAnnotationClass" in state.annotation.data.selectedAnnotationType &&
      state.annotation.data.selectedAnnotationType.pointAnnotationClass
        ? state.annotation.data.selectedAnnotationType.pointAnnotationClass.id
        : null;

    if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
      await createAnnotationApi(
        pointToGeoJSON(params.point),
        params.hpfRadius,
        null,
        null,
        null,
        pointClass,
        "POINT",
        state.imageWithAnnotations.resource.imageAndQuery.image.id,
        null
      ).then(response => {
        const pt = response as PointAnnotation;
        const hpfId = pt.parent || "no-id2";

        const topLevelAnnotations = mapStateToFragments(rootState);
        const anno = topLevelAnnotations.find(x => x.hpfs?.findIndex(y => y.id === hpfId) !== -1);

        if (
          anno &&
          state.manualHpfVisibility.find(
            x =>
              x.pointClassId === pointClass ||
              (x.hpfId === hpfId && x.pointClassId === null) ||
              (x.hpfClassid === anno.id && x.hpfId === null && x.pointClassId === null)
          )
        ) {
          dispatch(
            toggleHpfVisibility({
              source: "CR",
              fragment: anno,
              hpfVisibility: {
                hpfClassid: anno.id || null,
                hpfId: hpfId,
                pointClassId: pointClass
              }
            })
          );
        }
      });

      await dispatch(
        createImageAnnotationSuccess({
          keepAnnotating: keepAnnotating,
          cleanupCallback: params.cleanupCallback
        })
      );
    }
  }
);

export const createAnnotationEllipse = createAsyncThunk(
  "caseImageViewer/createAnnotationEllipse",
  async (cleanupCallback: () => void, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;

    const keepAnnotating = false;

    if (
      state.imageWithAnnotations &&
      "resource" in state.imageWithAnnotations &&
      state.annotation.data &&
      state.annotation.data.form
    ) {
      await createAnnotationApi(
        pointToGeoJSON(state.annotation.data.form.geometry),
        "radiusX" in state.annotation.data.form ? state.annotation.data.form.radiusX : null,
        "radiusY" in state.annotation.data.form ? state.annotation.data.form.radiusY : null,
        "tilt" in state.annotation.data.form ? state.annotation.data.form.tilt : null,
        "text" in state.annotation.data.form ? state.annotation.data.form.text : null,
        null,
        "ELLIPSE",
        state.imageWithAnnotations.resource.imageAndQuery.image.id,
        null
      );

      await dispatch(
        createImageAnnotationSuccess({
          keepAnnotating: keepAnnotating,
          cleanupCallback: cleanupCallback
        })
      );
    }
  }
);

export interface CreateHpfImageAnnotationParams {
  point: LatLngLiteral;
  radius: number;
  cleanupCallback: () => void;
}
export const createHpfImageAnnotation = createAsyncThunk(
  "caseImageViewer/createHpfImageAnnotation",
  async (params: CreateHpfImageAnnotationParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;

    const keepAnnotating = true;

    if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
      const response = await createAnnotationApi(
        pointToGeoJSON(params.point),
        params.radius,
        null,
        null,
        null,
        state.annotation.data &&
          "hpfAnnotationClass" in state.annotation.data.selectedAnnotationType &&
          state.annotation.data.selectedAnnotationType.hpfAnnotationClass
          ? state.annotation.data.selectedAnnotationType.hpfAnnotationClass.id
          : null,
        "HPF",
        state.imageWithAnnotations.resource.imageAndQuery.image.id,
        null
      );

      await dispatch(
        createImageAnnotationSuccess({
          keepAnnotating: keepAnnotating,
          cleanupCallback: params.cleanupCallback
        })
      );

      return response;
    }
  }
);

export const createAnnotation = createAsyncThunk(
  "caseImageViewer/createAnnotation",
  async (cleanupCallback: () => void, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;

    if (
      state.imageWithAnnotations &&
      "resource" in state.imageWithAnnotations &&
      state.annotation.data &&
      state.annotation.data.form
    ) {
      await createAnnotationApi(
        pointToGeoJSON(state.annotation.data.form.geometry),
        "radiusX" in state.annotation.data.form ? state.annotation.data.form.radiusX : null,
        "radiusY" in state.annotation.data.form ? state.annotation.data.form.radiusY : null,
        "tilt" in state.annotation.data.form ? state.annotation.data.form.tilt : null,
        "text" in state.annotation.data.form ? state.annotation.data.form.text : null,
        null,
        "TEXT",
        state.imageWithAnnotations.resource.imageAndQuery.image.id,
        null
      );

      if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
        await dispatch(
          createImageAnnotationSuccess({ keepAnnotating: false, cleanupCallback: cleanupCallback })
        );
      }
    }
  }
);

export interface CreateAnnotationSuccessParams {
  keepAnnotating: boolean;
  cleanupCallback: () => void;
}
export const createImageAnnotationSuccess = createAsyncThunk(
  "caseImageViewer/createImageAnnotationSuccess",
  async (params: CreateAnnotationSuccessParams, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;

    if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
      // NOTE: Placing of annotations continues until the user elects to stop for certain
      // annotation types like point, freehand, line
      if (!params.keepAnnotating) {
        dispatch(resetAnnotation());
      }
      await dispatch(
        refreshImage({ imageId: state.imageWithAnnotations.resource.imageAndQuery.image.id })
      );
      params.cleanupCallback();
    }
  }
);

export interface DeleteAnnotationParam {
  readonly annotationId: string;
  readonly deleteNested: boolean;
}

export const deleteAnnotation = createAsyncThunk(
  "caseImageViewer/deleteAnnotation",
  async (params: DeleteAnnotationParam, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const rootState = getState() as RootState;
    const state = rootState.caseImageViewer;
    const response = await deleteAnnotationApi(params.annotationId, params.deleteNested);

    if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
      await dispatch(
        refreshImage({ imageId: state.imageWithAnnotations.resource.imageAndQuery.image.id })
      );
    }
    return response;
  }
);

export const reorderImages = createAsyncThunk(
  "caseImageViewer/reorderImages",
  async (
    params: {
      readonly caseId: UUID;
      readonly imageOrders: ReadonlyArray<{ readonly imageId: UUID; readonly sortOrder: number }>;
    },
    thunkApi
  ) => {
    const { dispatch } = thunkApi;
    await reorderCaseImages(params.caseId, params.imageOrders);
    await dispatch(refreshCase());
  }
);

// The id of the image currently loaded in the viewer, or null. Used by the IQA reducers to discard
// responses for an image the user has already switched away from.
function currentSelectedImageId(state: CaseImageViewerState): UUID | null {
  return state.imageWithAnnotations !== null && "resource" in state.imageWithAnnotations
    ? state.imageWithAnnotations.resource.imageAndQuery.image.id
    : null;
}

export const caseImageViewerSlice = createSlice({
  name: "caseImageViewer",
  initialState: initialState,
  reducers: {
    resetCaseImageViewer: () => {
      return initialState;
    },
    resetImage: state => {
      state.imageWithAnnotations = null;
    },
    // FE-06: set which CR read the adjudicator is viewing (null = own read).
    setSelectedCrUserId: (state, action: PayloadAction<UUID | null>) => {
      state.selectedCrUserId = action.payload;
    },
    setZoom: (state, action) => {
      state.zoom = action.payload;
    },
    adjustBrightness: (state, action: PayloadAction<number>) => {
      state.brightnessAdjust = action.payload;
    },
    adjustAnnotationOpacity: (state, action: PayloadAction<number>) => {
      state.annotationOpacity = action.payload;
    },
    toggleSidebarExpanded: state => {
      state.isSidebarExpanded = !state.isSidebarExpanded;
    },
    toggleMicroscropeActive: state => {
      state.isMicroscopeActive = !state.isMicroscopeActive;
    },
    toggleAnnotations: state => {
      state.hideAnnotations = !state.hideAnnotations;
    },
    // annotations
    beginAnnotation: (state, action: PayloadAction<BeginAnnotationParams>) => {
      state.annotation = {
        data: {
          form: null,
          status: AnnotationStatus.Placing,
          selectedAnnotationType: action.payload.selectedAnnotationType
        }
      };
    },
    setBeginDroppingAnnotation: (state, action: PayloadAction<BeginDroppingAnnotationParams>) => {
      state.annotation = {
        data: {
          form: null,
          status: AnnotationStatus.Dropping,
          selectedAnnotationType: {
            ...action.payload.selectedAnnotationType
          }
        } as AnnotationForm<typeof action.payload.selectedAnnotationType>
      };
      state.hpfCursorColor = (action.payload
        .selectedAnnotationType as any).hpfAnnotationClass.color;
    },
    setImageAnnotationForEllipseType: (
      state,
      action: PayloadAction<SetImageAnnotationForEllipseTypeParams>
    ) => {
      if (state.annotation.data) {
        state.annotation.data = {
          form: {
            geometry: action.payload.point,
            radiusX: action.payload.radiusX,
            radiusY: action.payload.radiusY,
            tilt: action.payload.tilt,
            text:
              state.annotation.data.form && "text" in state.annotation.data.form
                ? state.annotation.data.form.text
                : null
          },
          status: AnnotationStatus.Editing,
          selectedAnnotationType: {
            type: AnnotationType.Ellipse
          }
        } as AnnotationForm<EllipseAnnotationType>;
      }
    },
    setImageAnnotationForHPFType: (
      state,
      action: PayloadAction<SetImageAnnotationForHPFTypeParams>
    ) => {
      if (state.annotation.data) {
        state.annotation.data = {
          form: {
            geometry: action.payload.point,
            radiusX: action.payload.radius,
            text:
              state.annotation.data.form && "text" in state.annotation.data.form
                ? state.annotation.data.form.text
                : null
          },
          status: AnnotationStatus.Editing,
          selectedAnnotationType: {
            type: AnnotationType.HPF,
            hpfAnnotationClass: (state.annotation.data.selectedAnnotationType as HpfAnnotationType)
              .hpfAnnotationClass
          }
        } as AnnotationForm<HpfAnnotationType>;
      }
    },
    setImageAnnotationForTextType: (state, action: PayloadAction<LatLngLiteral>) => {
      if (state.annotation.data) {
        state.annotation.data = {
          form: {
            geometry: action.payload,
            text:
              state.annotation.data.form && "text" in state.annotation.data.form
                ? state.annotation.data.form.text
                : null
          },
          status: AnnotationStatus.Editing,
          selectedAnnotationType: {
            type: AnnotationType.Text
          }
        } as AnnotationForm<TextAnnotationType>;
      }
    },
    setImageAnnotationText: (state, action: PayloadAction<string>) => {
      if (state.annotation.data && state.annotation.data.form) {
        state.annotation = {
          data: {
            ...state.annotation.data,
            form: {
              ...state.annotation.data.form,
              text: action.payload
            }
          }
        };
      }
    },
    // Hovering a sidebar row only records which ids are highlighted. The weight each point or HPF
    // is drawn with is derived from these ids at render time (see `annotationWeight`), so a hover
    // no longer copies every annotation or invalidates the annotation cache key.
    setImageAnnotationHighlight: (state, action: PayloadAction<string>) => {
      state.highlightedAnnotations = castDraft(addId(state.highlightedAnnotations, action.payload));
    },
    setImageAnnotationUnhighlight: (state, action: PayloadAction<string>) => {
      state.highlightedAnnotations = castDraft(
        removeId(state.highlightedAnnotations, action.payload)
      );
    },
    cancelImageAnnotation: state => {
      state.annotation = initialState.annotation;
    },
    resetAnnotation: state => {
      state.annotation = initialState.annotation;
    },
    setSelectedMultilineClass: (state, action: PayloadAction<MultilineAnnotationClass | null>) => {
      state.selectedMultilineClass = action.payload;
    },
    setSelectedMultilineCategory: (state, action: PayloadAction<MultilineCategory | null>) => {
      state.selectedMultilineCategory = action.payload;
    },
    toggleHpfVisibility: (state, action: PayloadAction<HpfVisibilityPayload>) => {
      const hpfVisibilityKey =
        action.payload.source.toUpperCase() === "CR" ? "manualHpfVisibility" : "aiHpfVisibility";
      const anno = action.payload.fragment;
      const hpfVisibility = action.payload.hpfVisibility;
      //check if all hpfs for the class are being hidden
      const hpfClassHidden =
        state[hpfVisibilityKey].findIndex(
          x =>
            x.hpfClassid === hpfVisibility.hpfClassid && x.hpfId === null && x.pointClassId === null
        ) !== -1;
      if (hpfClassHidden && hpfVisibility.hpfId === null && hpfVisibility.pointClassId === null) {
        //show hpfs by class - remove from both visibility arrays
        state.manualHpfVisibility = state.manualHpfVisibility.filter(
          x => x.hpfClassid !== hpfVisibility.hpfClassid
        );
        state.aiHpfVisibility = state.aiHpfVisibility.filter(
          x => x.hpfClassid !== hpfVisibility.hpfClassid
        );
      } else {
        //check if the HPF is being hidden by ID
        const hpfIdHidden =
          state[hpfVisibilityKey].findIndex(
            x =>
              x.hpfClassid === hpfVisibility.hpfClassid &&
              x.hpfId === hpfVisibility.hpfId &&
              x.pointClassId === null
          ) !== -1;
        if ((hpfClassHidden || hpfIdHidden) && hpfVisibility.pointClassId === null) {
          //check if entire hpf class is being hidden
          if (hpfClassHidden) {
            //show the class but hide all of the hpf ids under it
            state[hpfVisibilityKey] = state[hpfVisibilityKey].filter(
              x => x.hpfClassid !== hpfVisibility.hpfClassid
            );
            anno.hpfs?.flatMap(hpf => {
              state[hpfVisibilityKey] = [
                ...state[hpfVisibilityKey],
                {
                  hpfClassid: hpfVisibility.hpfClassid,
                  hpfId: hpf.id,
                  pointClassId: null
                }
              ];
            });
          }

          //show hpf by id - remove from both visibility arrays since HPF affects all children
          state.manualHpfVisibility = state.manualHpfVisibility.filter(
            x => x.hpfId !== hpfVisibility.hpfId
          );
          state.aiHpfVisibility = state.aiHpfVisibility.filter(
            x => x.hpfId !== hpfVisibility.hpfId
          );
        } else {
          //check if the points are being hidden by their class
          const pointClassHidden =
            state[hpfVisibilityKey].findIndex(
              x =>
                x.hpfClassid === hpfVisibility.hpfClassid &&
                x.hpfId === hpfVisibility.hpfId &&
                x.pointClassId === hpfVisibility.pointClassId
            ) !== -1;
          if (hpfClassHidden || hpfIdHidden || pointClassHidden) {
            //check if entire hpf class has been hidden
            if (hpfClassHidden) {
              //show the class but hide all of the hpf ids under it
              state[hpfVisibilityKey] = state[hpfVisibilityKey].filter(
                x => x.hpfClassid !== hpfVisibility.hpfClassid
              );
              anno.hpfs?.flatMap(hpf => {
                state[hpfVisibilityKey] = [
                  ...state[hpfVisibilityKey],
                  {
                    hpfClassid: hpfVisibility.hpfClassid,
                    hpfId: hpf.id,
                    pointClassId: null
                  }
                ];
              });

              //show the hpf but hide all of the point classes under it
              state[hpfVisibilityKey] = state[hpfVisibilityKey].filter(
                x => x.hpfId !== hpfVisibility.hpfId
              );
              const hpf = anno.hpfs?.find(x => x.id === hpfVisibility.hpfId);
              const isAiSource = action.payload.source === "VisioPharm";
              hpf?.points
                ?.filter(point => point.name.includes("-AI") === isAiSource)
                .flatMap(point => {
                  state[hpfVisibilityKey] = [
                    ...state[hpfVisibilityKey],
                    {
                      hpfClassid: hpfVisibility.hpfClassid,
                      hpfId: hpf.id,
                      pointClassId: point.id
                    }
                  ];
                });
            }

            //check if entire HPF has been hidden
            if (hpfIdHidden) {
              //show the hpf but hide all of the point classes under it
              state[hpfVisibilityKey] = state[hpfVisibilityKey].filter(
                x => x.hpfId !== hpfVisibility.hpfId
              );
              const hpf = anno.hpfs?.find(x => x.id === hpfVisibility.hpfId);
              const isAiSource = action.payload.source === "VisioPharm";
              hpf?.points
                ?.filter(point => point.name.includes("-AI") === isAiSource)
                .flatMap(point => {
                  state[hpfVisibilityKey] = [
                    ...state[hpfVisibilityKey],
                    {
                      hpfClassid: hpfVisibility.hpfClassid,
                      hpfId: hpf.id,
                      pointClassId: point.id
                    }
                  ];
                });
            }

            //show points by class
            state[hpfVisibilityKey] = state[hpfVisibilityKey].filter(
              x =>
                x.hpfClassid !== hpfVisibility.hpfClassid ||
                x.hpfId !== hpfVisibility.hpfId ||
                x.pointClassId !== hpfVisibility.pointClassId
            );
          } else {
            //hide selected
            if (hpfVisibility.hpfId === null && hpfVisibility.pointClassId === null) {
              // Fragment level - add to both arrays
              state.manualHpfVisibility = [...state.manualHpfVisibility, hpfVisibility];
              state.aiHpfVisibility = [...state.aiHpfVisibility, hpfVisibility];
            } else {
              // HPF or point level - add to specific array
              state[hpfVisibilityKey] = [...state[hpfVisibilityKey], hpfVisibility];
            }
          }
        }
      }
    },
    toggleCeliacTableVisibility: state => {
      state.celiacTableVisibility = !state.celiacTableVisibility;
    }
  },
  extraReducers: builder => {
    // caseFetch
    builder.addCase(caseFetch.pending, state => {
      state.histoCase = { isPending: true };
      state.imageWithAnnotations = null;
      state.annotationCacheKeyAsUnixTs = Date.now();
    });
    builder.addCase(caseFetch.fulfilled, (state, action) => {
      state.histoCase = { resource: castDraft(action.payload) };
    });
    builder.addCase(caseFetch.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // FE-05: store the case's biopsy groupings for the image selector.
    builder.addCase(biopsiesFetch.fulfilled, (state, action) => {
      state.biopsies = castDraft(action.payload);
    });
    builder.addCase(biopsiesFetch.rejected, state => {
      state.biopsies = [];
    });

    // IQA: deficient-feature options (static list); clear on failure.
    builder.addCase(deficientFeatureOptionsFetch.fulfilled, (state, action) => {
      state.deficientFeatureOptions = castDraft(action.payload);
    });
    builder.addCase(deficientFeatureOptionsFetch.rejected, state => {
      state.deficientFeatureOptions = [];
    });

    // IQA: the selected image's loaded saved assessment (null payload = none recorded yet). A fresh
    // fetch clears any lingering save error.
    builder.addCase(imageQualityAssessmentFetch.pending, state => {
      state.imageQualityAssessment = { isPending: true };
      state.imageQualityAssessmentSaveError = null;
    });
    builder.addCase(imageQualityAssessmentFetch.fulfilled, (state, action) => {
      // Requests are not cancelled, so a response for a previously-selected image can arrive after
      // the user has switched images. Drop it unless it still matches the current selection — else the
      // form would pre-fill with the wrong image's IQA and a later Save would write it under the
      // now-selected image's id. Mirrors the imageFetch.pending action.meta.arg guard.
      if (currentSelectedImageId(state) === action.meta.arg.imageId) {
        state.imageQualityAssessment = { resource: castDraft(action.payload) };
      }
    });
    builder.addCase(imageQualityAssessmentFetch.rejected, (state, action) => {
      // Same stale-response guard as the fulfilled case: requests are not cancelled, so a fetch for a
      // previously-selected image can fail (timeout, transient 5xx) after the user has switched and
      // the new image's IQA already loaded. Ignore a rejection for an image that is no longer
      // selected, so it can't clobber the correctly-loaded current image with a spurious error.
      if (currentSelectedImageId(state) !== action.meta.arg.imageId) {
        return;
      }
      // A rejectWithValue failure (e.g. "No case is loaded.") carries its message in payload, not
      // action.error (which is the generic "Rejected"); prefer the payload so the real cause shows.
      const message =
        typeof action.payload === "string" ? action.payload : action.error.message || "";
      state.imageQualityAssessment = { errorMessage: message };
    });

    // IQA save: tracked separately so an in-flight or failed save does not clobber the loaded value.
    // On success the persisted assessment (returned by the thunk's re-fetch) replaces the loaded one.
    builder.addCase(imageQualityAssessmentSave.pending, state => {
      state.imageQualityAssessmentSaving = true;
      state.imageQualityAssessmentSaveError = null;
    });
    builder.addCase(imageQualityAssessmentSave.fulfilled, (state, action) => {
      state.imageQualityAssessmentSaving = false;
      // Same stale-response guard as the fetch: only reflect the saved value if its image is still
      // the selected one.
      if (currentSelectedImageId(state) === action.meta.arg.imageId) {
        state.imageQualityAssessment = { resource: castDraft(action.payload) };
      }
    });
    builder.addCase(imageQualityAssessmentSave.rejected, (state, action) => {
      state.imageQualityAssessmentSaving = false;
      state.imageQualityAssessmentSaveError = action.error.message || "Failed to save.";
    });

    // FE-06: adjudicator read-switching state. Default the viewed read to the first CR (Reader 1);
    // the component switches to the adjudicator's own read for Hybrid. On failure (not the
    // adjudicator) clear it so no switcher is shown.
    //
    // Only reset selectedCrUserId when the current selection is no longer valid for the new reads
    // (e.g. initial load, or a reader dropped out). Preserving a still-valid selection avoids
    // desyncing it from the annotations already displayed for that reader: this thunk does not
    // re-fetch the image, so blindly snapping back to Reader 1 would leave the sidebar showing
    // "Reader 1" while the tile viewer still shows whoever the adjudicator was viewing (e.g. after
    // recording a selection while viewing Reader 2, or before the initial image re-fetch resolves).
    builder.addCase(adjudicationReadsFetch.fulfilled, (state, action) => {
      state.adjudicationReads = castDraft(action.payload);
      const currentIsValid =
        (state.selectedCrUserId === null && action.payload.adjudicatorAssignmentId !== null) ||
        action.payload.reads.some(r => r.crUserId === state.selectedCrUserId);
      const firstRead = action.payload.reads[0];
      state.selectedCrUserId = currentIsValid
        ? state.selectedCrUserId
        : firstRead
        ? firstRead.crUserId
        : null;
    });
    builder.addCase(adjudicationReadsFetch.rejected, state => {
      state.adjudicationReads = null;
      state.selectedCrUserId = null;
    });

    // refreshCase
    builder.addCase(refreshCase.fulfilled, (state, action) => {
      const selectedImageId =
        state.imageWithAnnotations && "resource" in state.imageWithAnnotations
          ? state.imageWithAnnotations.resource.imageAndQuery.image.id
          : null;

      const isSelectedImageInCase =
        selectedImageId !== null &&
        action.payload &&
        action.payload.images.map(image => image.id).includes(selectedImageId);

      if (action.payload) {
        state.histoCase = { resource: castDraft(action.payload) };
      }
      state.imageWithAnnotations = isSelectedImageInCase ? state.imageWithAnnotations : null;
      state.allHighlightedAnnotations = castDraft(highlightAll(state.imageWithAnnotations, []));
      state.annotationCacheKeyAsUnixTs = Date.now();
    });
    builder.addCase(refreshCase.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // refreshImage
    builder.addCase(refreshImage.fulfilled, (state, action) => {
      state.imageWithAnnotations = { resource: castDraft(action.payload) };
      state.allHighlightedAnnotations = castDraft(highlightAllImage(action.payload, []));
      state.annotationCacheKeyAsUnixTs = Date.now();

      // Initialize orphan AI visibility for Celiac studies — hides AI orphan points by default
      // until they are inside a freehand. Manual orphan points are always visible.
      const hasCeliacIndication = action.payload.study?.indications.includes(
        Indication.CeliacDisease
      );
      if (hasCeliacIndication) {
        const orphanVisibility = {
          hpfClassid: "no-id",
          hpfId: "no-id2",
          pointClassId: null
        };

        const aiHasOrphan = state.aiHpfVisibility.some(
          v => v.hpfClassid === "no-id" && v.hpfId === "no-id2" && v.pointClassId === null
        );

        if (!aiHasOrphan) {
          state.aiHpfVisibility = [...state.aiHpfVisibility, orphanVisibility];
        }
      }
    });
    builder.addCase(refreshImage.rejected, (state, action) => {
      state.imageWithAnnotations = { errorMessage: action.error.message || "" };
      state.annotationCacheKeyAsUnixTs = Date.now();
    });

    // transitionCaseStatus
    builder.addCase(transitionCaseStatus.fulfilled, (state, action) => {
      if (action.payload) {
        state.histoCase = { resource: castDraft(action.payload) };
      }
    });
    builder.addCase(transitionCaseStatus.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // completeRead — on success stamp the current reader's readCompletedAt onto the loaded case so
    // the "Mark as Scored" button hides immediately (the thunk also redirects; this keeps state
    // correct if navigation is suppressed). On failure surface it (409 already-completed, 403,
    // network) instead of silently swallowing.
    builder.addCase(completeRead.fulfilled, (state, action) => {
      if (
        action.payload &&
        state.histoCase !== null &&
        "resource" in state.histoCase &&
        "readCompletedAt" in state.histoCase.resource
      ) {
        state.histoCase.resource.readCompletedAt = castDraft(action.payload.readCompletedAt);
      }
    });
    builder.addCase(completeRead.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // reorderImages — a rejected reorder (e.g. 400 when the case is not Pending Lab QC or Pending
    // QC) must surface instead of silently doing nothing. On success the thunk refreshes the case,
    // so no fulfilled reducer.
    builder.addCase(reorderImages.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // selectImage
    builder.addCase(selectImage.pending, state => {
      state.queryEditor = initialState.queryEditor;
      state.imageWithAnnotations = null;
    });

    builder.addCase(imageFetch.pending, (state, action) => {
      // FE-06: an adjudicator switching which CR's read they view re-fetches the SAME image scoped by
      // crUserId. Blanking imageWithAnnotations to { isPending } during that re-fetch unmounts the
      // case view (Case.tsx treats a pending image as a full-page load), which resets the sidebar to
      // the Overview panel — so the adjudicator could never stay on the Adjudication panel. Keep the
      // already-loaded image in place while re-fetching it (stale-while-revalidate); only show the
      // pending spinner for the initial load or when switching to a DIFFERENT image.
      const currentImageId =
        state.imageWithAnnotations !== null && "resource" in state.imageWithAnnotations
          ? state.imageWithAnnotations.resource.imageAndQuery.image.id
          : null;
      const isSameImageRefetch = currentImageId !== null && action.meta.arg === currentImageId;
      if (!isSameImageRefetch) {
        state.imageWithAnnotations = { isPending: true };
      }
      state.annotationCacheKeyAsUnixTs = Date.now();
    });
    builder.addCase(imageFetch.fulfilled, (state, action) => {
      state.imageWithAnnotations = { resource: castDraft(action.payload) };
      state.allHighlightedAnnotations = castDraft(highlightAllImage(action.payload, []));
      state.annotationCacheKeyAsUnixTs = Date.now();

      // Initialize orphan AI visibility for Celiac studies — hides AI orphan points by default
      // until they are inside a freehand. Manual orphan points are always visible.
      const hasCeliacIndication = action.payload.study?.indications.includes(
        Indication.CeliacDisease
      );
      if (hasCeliacIndication) {
        const orphanVisibility = {
          hpfClassid: "no-id",
          hpfId: "no-id2",
          pointClassId: null
        };

        const aiHasOrphan = state.aiHpfVisibility.some(
          v => v.hpfClassid === "no-id" && v.hpfId === "no-id2" && v.pointClassId === null
        );

        if (!aiHasOrphan) {
          state.aiHpfVisibility = [...state.aiHpfVisibility, orphanVisibility];
        }
      }
    });
    builder.addCase(toggleAcceptAiAnnotations.fulfilled, (state, action) => {
      /*const newAcceptAi = castDraft(action.payload).acceptAiAnnotations;
      if (state && state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
        state.imageWithAnnotations = {
          ...state.imageWithAnnotations,
          resource: {
            ...state.imageWithAnnotations.resource,
            acceptAiAnnotations: newAcceptAi
          }
        };
      }*/
      const { acceptAiAnnotations } = action.payload;

      if ((state?.imageWithAnnotations as any)?.resource) {
        (state.imageWithAnnotations as any).resource.acceptAiAnnotations = acceptAiAnnotations;
      }
    });
    builder.addCase(imageFetch.rejected, (state, action) => {
      state.imageWithAnnotations = { errorMessage: action.error.message || "" };
      state.annotationCacheKeyAsUnixTs = Date.now();
    });

    // hideImageForReader
    builder.addCase(toggleImageForCR.pending, state => {
      state.imageWithAnnotations = { isPending: true };
      state.annotationCacheKeyAsUnixTs = Date.now();
    });
    builder.addCase(toggleImageForCR.rejected, (state, action) => {
      state.imageWithAnnotations = { errorMessage: action.error.message || "" };
      state.annotationCacheKeyAsUnixTs = Date.now();
    });

    // queries

    // updateCaseComment
    builder.addCase(updateCaseComment.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // updateImageComment
    builder.addCase(updateImageComment.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // openQuery
    builder.addCase(openQuery.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // closeQuery
    builder.addCase(closeQuery.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // unresolvableQuery
    builder.addCase(unresolvableQuery.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // newQueryReminder
    builder.addCase(newQueryReminder.rejected, (state, action) => {
      state.histoCase = { errorMessage: action.error.message || "" };
    });

    // annotations

    // deleteAnnotation
    builder.addCase(deleteAnnotation.fulfilled, state => {
      state.annotation = initialState.annotation;
      state.annotationCacheKeyAsUnixTs = Date.now();
    });
    builder.addCase(deleteAnnotation.rejected, (state, action) => {
      state.annotation = {
        data: null,
        errorMessage: action.error.message || ""
      };
    });

    // createHpfImageAnnotation
    builder.addCase(createHpfImageAnnotation.pending, (state, action) => {
      state.tempHpf = action.meta.arg.point;
    });
    builder.addCase(createHpfImageAnnotation.fulfilled, state => {
      if (state.imageWithAnnotations && "resource" in state.imageWithAnnotations) {
        state.tempHpf = null;
      }
      state.annotationCacheKeyAsUnixTs = Date.now();
    });
    builder.addCase(createHpfImageAnnotation.rejected, (state, action) => {
      state.annotation = {
        data: state.annotation.data,
        errorMessage: action.error.message || ""
      };
    });

    // createAnnotation
    builder.addCase(createAnnotation.rejected, (state, action) => {
      state.annotation = {
        data: state.annotation.data,
        errorMessage: action.error.message
      };
    });
  }
});

export const {
  resetCaseImageViewer,
  resetImage,
  setSelectedCrUserId,
  setZoom, //setSelectedImage, setImageMenuOverflow,
  adjustBrightness,
  adjustAnnotationOpacity,
  toggleSidebarExpanded,
  toggleMicroscropeActive,
  toggleAnnotations,
  beginAnnotation,
  setBeginDroppingAnnotation,
  setImageAnnotationForEllipseType,
  setImageAnnotationForHPFType,
  setImageAnnotationForTextType,
  setImageAnnotationText,
  setImageAnnotationHighlight,
  setImageAnnotationUnhighlight,
  cancelImageAnnotation,
  resetAnnotation,
  setSelectedMultilineClass,
  setSelectedMultilineCategory,
  toggleHpfVisibility,
  toggleCeliacTableVisibility
} = caseImageViewerSlice.actions;

export default caseImageViewerSlice.reducer;
